import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Home as HomeIcon, IndianRupee } from "lucide-react";
import { CITIES_ALL, PROPERTY_TYPES } from "@/lib/brand";

// Tabs shown in the hero. `badge` renders a small pill next to the label;
// `route` (if present) sends users to a page instead of the properties list;
// `hero: "primary"` gives BUY / SELL a permanent gold-line highlight so users
// immediately see the two anchor intents even before hovering / clicking.
const TABS = [
    { k: "buy",       l: "Buy",                                  hero: "primary" },
    { k: "sell",      l: "Sell",       route: "/contact?intent=sell", hero: "primary" },
    { k: "rent",      l: "Rent" },
    { k: "builder",   l: "Builder Projects",   badge: "NEW" },
    { k: "auction",   l: "Auction Properties", badge: "NEW" },
    { k: "commercial",l: "Brand for Commercial", route: "/brands" },
    { k: "partners",  l: "Channel Partners",   route: "/partner-registration" },
];

const CITIES = ["All Cities", ...CITIES_ALL];
const TYPES  = ["All Types", ...PROPERTY_TYPES];
const BUDGETS = [
    { l: "Any Budget",       min: "", max: "" },
    { l: "Under ₹50 L",      min: "0",   max: "0.5" },
    { l: "₹50 L – ₹1 Cr",    min: "0.5", max: "1" },
    { l: "₹1 Cr – ₹3 Cr",    min: "1",   max: "3" },
    { l: "₹3 Cr – ₹5 Cr",    min: "3",   max: "5" },
    { l: "₹5 Cr – ₹10 Cr",   min: "5",   max: "10" },
    { l: "Above ₹10 Cr",     min: "10",  max: "" },
];

const POPULAR = ["3 BHK in Mohali", "Villas in Chandigarh", "New Launch in Gurugram", "Luxury Homes in Mumbai", "Commercial in Bangalore"];

export default function HeroSearchBar() {
    const nav = useNavigate();
    const [tab, setTab] = useState("buy");
    const [city, setCity] = useState("All Cities");
    const [type, setType] = useState("All Types");
    const [budget, setBudget] = useState(BUDGETS[0]);

    const active = TABS.find((t) => t.k === tab) || TABS[0];
    // Route-based tabs (Sell, Channel Partners) replace the filter row with a
    // full-width CTA that navigates on click — filter inputs don't apply.
    const isRouteTab = !!active.route;

    const search = () => {
        if (active.route) { nav(active.route); return; }
        const q = new URLSearchParams();
        if (tab === "rent")           q.set("listing_type", "rent");
        else if (tab === "builder")   q.set("cat", "under");
        else if (tab !== "auction")   q.set("listing_type", "sale");                     // buy (sell tab uses route)
        if (city !== "All Cities") q.set("city", city);
        if (type !== "All Types") q.set("type", type);
        if (tab === "auction") { nav(`/auction-properties?${q.toString()}`); return; }
        if (budget.min) q.set("min_price", budget.min);
        if (budget.max) q.set("max_price", budget.max);
        nav(`/properties?${q.toString()}`);
    };

    return (
        <div data-testid="hero-search-bar" className="w-full max-w-4xl">
            {/* Tabs */}
            <div className="flex flex-wrap gap-0 no-scrollbar">
                {TABS.map((t) => {
                    const isActive = tab === t.k;
                    // Primary tabs (Buy / Sell) get a gold underline + slightly warmer bg
                    // to stand out even when inactive — the two anchor conversion actions.
                    const inactiveCls = t.hero === "primary"
                        ? "bg-[var(--ebg-gold)]/15 text-white hover:bg-[var(--ebg-gold)]/25 border border-[var(--ebg-gold)]/50 shadow-[inset_0_-2px_0_0_var(--ebg-gold)]"
                        : "bg-black/40 backdrop-blur-md text-white/85 hover:bg-black/60 border border-white/10";
                    return (
                        <button
                            key={t.k}
                            data-testid={`search-tab-${t.k}`}
                            onClick={() => setTab(t.k)}
                            className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold px-5 py-3 whitespace-nowrap transition-all ${
                                isActive ? "bg-white/95 text-black" : inactiveCls
                            }`}
                        >
                            <span>{t.l}</span>
                            {t.badge && (
                                <span
                                    data-testid={`search-tab-${t.k}-badge`}
                                    className={`inline-flex items-center text-[8.5px] tracking-[0.14em] font-bold uppercase leading-none px-1.5 py-[3px] rounded-sm ${
                                        isActive ? "bg-black text-[var(--ebg-gold)]" : "bg-[var(--ebg-gold)] text-black"
                                    }`}
                                >
                                    {t.badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Search bar OR route-tab CTA row */}
            {isRouteTab ? (
                <RouteTabCta tab={tab} onClick={search}/>
            ) : (
                <div className="bg-white/95 backdrop-blur-xl p-2 md:p-3 flex flex-col md:flex-row gap-2 md:gap-0 md:items-stretch shadow-2xl">
                    <SelectField
                        icon={MapPin}
                        label="City"
                        value={city}
                        options={CITIES}
                        onChange={setCity}
                        testid="search-city"
                    />
                    <Divider />
                    <SelectField
                        icon={HomeIcon}
                        label="Property Type"
                        value={type}
                        options={TYPES}
                        onChange={setType}
                        testid="search-type"
                    />
                    <Divider />
                    <SelectField
                        icon={IndianRupee}
                        label="Budget"
                        value={budget.l}
                        options={BUDGETS.map((b) => b.l)}
                        onChange={(l) => setBudget(BUDGETS.find((b) => b.l === l) || BUDGETS[0])}
                        testid="search-budget"
                    />
                    <button
                        onClick={search}
                        data-testid="search-submit"
                        className="bg-[var(--ebg-gold)] text-black px-8 py-4 md:py-0 text-[11px] uppercase tracking-[0.28em] font-bold inline-flex items-center justify-center gap-2 hover:bg-black hover:text-[var(--ebg-gold)] transition-colors"
                    >
                        <Search size={14}/> Search Properties
                    </button>
                </div>
            )}

            {/* Popular */}
            <div className="flex flex-wrap items-center gap-2 mt-5">
                <span className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/60">Popular:</span>
                {POPULAR.map((p, i) => (
                    <button
                        key={i}
                        data-testid={`popular-${i}`}
                        onClick={() => {
                            const pick = p.includes("Mohali") ? "Mohali"
                                : p.includes("Chandigarh") ? "Chandigarh"
                                : p.includes("Mumbai") ? "Mumbai"
                                : p.includes("Bangalore") ? "Bengaluru"
                                : "All Cities";
                            setCity(pick);
                            // trigger search after state update
                            setTimeout(search, 0);
                        }}
                        className="text-[11px] px-3 py-1.5 border border-white/20 text-white/80 hover:border-[var(--ebg-gold)] hover:text-[var(--ebg-gold)] transition-colors"
                    >
                        {p}
                    </button>
                ))}
            </div>
        </div>
    );
}

function Divider() {
    return <span className="hidden md:block w-px bg-black/10 my-2"/>;
}

const ROUTE_CTA_CONTENT = {
    sell: {
        eyebrow: "List With EBG",
        heading: "Sell your property with the trust network India's buyers prefer.",
        sub: "Zero listing fee, in-house legal desk, and pre-verified buyer pool — get an accurate valuation & first enquiry within 48 hours.",
        button: "Start Sell Enquiry",
        testid: "hero-sell-cta",
        btnTestid: "hero-sell-submit",
    },
    commercial: {
        eyebrow: "Brand for Commercial",
        heading: "Franchise & lease opportunities with established brands.",
        sub: "Explore brands looking for commercial space — call or WhatsApp our commercial desk to get connected.",
        button: "Explore All Brands",
        testid: "hero-brands-cta",
        btnTestid: "hero-brands-submit",
    },
    partners: {
        eyebrow: "Channel Partner Programme",
        heading: "Join India's most trusted brokerage network.",
        sub: "Verified listings, higher payouts, a dedicated relationship desk — apply in under 10 minutes.",
        button: "Start Partner Application",
        testid: "hero-partner-cta",
        btnTestid: "hero-partner-submit",
    },
};

function RouteTabCta({ tab, onClick }) {
    const c = ROUTE_CTA_CONTENT[tab] || ROUTE_CTA_CONTENT.partners;
    return (
        <div
            data-testid={c.testid}
            className="bg-white/95 backdrop-blur-xl p-4 md:p-5 flex flex-col md:flex-row gap-4 md:gap-6 md:items-center shadow-2xl"
        >
            <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">{c.eyebrow}</div>
                <div className="mt-1 font-serif text-lg md:text-xl text-black leading-snug">{c.heading}</div>
                <div className="text-[12px] text-black/60 mt-0.5">{c.sub}</div>
            </div>
            <button
                onClick={onClick}
                data-testid={c.btnTestid}
                className="shrink-0 bg-[var(--ebg-gold)] text-black px-8 py-4 text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] transition-colors inline-flex items-center justify-center gap-2"
            >
                <Search size={14}/> {c.button}
            </button>
        </div>
    );
}

function SelectField({ icon: Icon, label, value, options, onChange, testid }) {
    return (
        <label className="flex-1 flex items-center gap-3 px-4 py-2 md:py-3 hover:bg-black/[0.02] transition-colors cursor-pointer">
            <Icon size={16} className="text-[var(--ebg-gold)] shrink-0"/>
            <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-[0.24em] font-bold text-black/45">{label}</div>
                <select
                    data-testid={testid}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full bg-transparent text-[13px] font-medium text-black outline-none appearance-none cursor-pointer truncate"
                >
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            </div>
        </label>
    );
}
